import {
  ColDef,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  Module,
  ProcessCellForExportParams,
} from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  HeliosColumnTypes,
  heliosDefaultColWidth,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { NfTooltipParams } from '@northfork/ag-grid'
import {
  composeFieldPath,
  formatDate,
  ISODateTimeFormat,
} from '@northfork/basics'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { JobsKey } from '../../../panel-key'
import { commandDataCellRenderer } from '../../grid-helpers/command-data-cell-renderer'
import { Command } from '../../model/job.model'
import { JobsService } from '../../services/jobs.service'
import { JsonReaderModalComponent } from '../json-reader-modal/json-reader-modal.component'

const CommandGridKey = `${JobsKey}-CommandGrid`

const CommandPath = composeFieldPath([] as Command[])
@Component({
  selector: 'hs-commands',
  templateUrl: './commands.component.html',
})
export class CommandsComponent implements OnDestroy {
  commands: Command[] = []
  gridOptions: GridOptions = this.createGridOptions()
  gridModules: Module[] = []
  /** @internal */
  gridApi?: GridApi

  private readonly destroySubject$ = new Subject<void>()
  isLoading = false

  private _jobId = [-1]

  @Input()
  set jobId(value: number[]) {
    this._jobId = value
    this.loadUserSubmittedCommands()
  }
  search = new FormControl('')
  @Output()
  selectedCommandsJobIds = new EventEmitter<string>()

  constructor(
    private readonly jobsService: JobsService,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly modalService: NgbModal,
  ) {}
  ngOnInit() {
    this.search.valueChanges.pipe(debounceTime(500)).subscribe(search => {
      this.gridApi?.setQuickFilter(search)
    })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.destroy()
  }

  loadUserSubmittedCommands() {
    this.gridModules = []
    this.gridOptions = this.createGridOptions()
    const jobIds = this._jobId.slice(0, 250)
    this.loadData(jobIds)
  }

  async loadData(jobIds: number[]) {
    this.isLoading = true
    const promises = jobIds.map(async jobId => {
      const commands = await this.jobsService.getCommands(jobId).toPromise()
      return commands.map(command => ({ ...command, jobId }))
    })
    const responses = await Promise.all(promises)
    this.commands = responses.flat() //flattening array by depth 0
    this.isLoading = false
  }

  createGridOptions(): GridOptions {
    return {
      ...heliosMultiRowSelectionConfig,
      enableBrowserTooltips: true,
      columnDefs: this.createBaseColumnDefs(),
      onGridReady: event => this.onGridReady(event),
      processCellForClipboard: (params: ProcessCellForExportParams) => {
        if (typeof params.value === 'object') {
          return JSON.stringify(params.value)
        }
        if (params.column.getColDef().type === 'DateTime' && params.value) {
          return formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat)
        }
        return params.value
      },
      onCellDoubleClicked: event => {
        if (event.colDef.field !== 'data') return
        this.launchJsonReaderModal(event.value)
      },
      getContextMenuItems: params => this.getContextMenuItems(params),
    }
  }

  private onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    this.savedViewService.manageSavedView(
      event,
      CommandGridKey,
      this.destroySubject$,
    )

    enableHeliosMultiRowDragSelection(this.gridApi)
    enableHeliosCopyBehavior(event.api)
  }

  createBaseColumnDefs(): ColDef[] {
    return [
      { field: CommandPath('type'), width: 400 },
      {
        field: CommandPath('data'),
        cellRenderer: commandDataCellRenderer,
        tooltipComponentParams: {
          showTooltip: () => false,
        } as NfTooltipParams,
        tooltipValueGetter: params => {
          return (
            JSON.stringify(params.value)
              .replace(/,"/g, '\n')
              .replace(/"/g, '') ?? ''
          )
        },
        filterParams: {
          textFormatter: (params: string) => {
            return JSON.stringify(params)
          },
        },
      },
      { field: CommandPath('userName'), width: heliosDefaultColWidth.Id },
      { field: CommandPath('note') },
      {
        field: CommandPath('timestamp'),
        type: HeliosColumnTypes.DateTime,
        valueFormatter: params =>
          formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat) ??
          params.value,
        width: heliosDefaultColWidth.DateTime,
      },
    ]
  }

  getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
    // select the row
    if (params.node && !params.node.isSelected()) {
      params.api.selectNode(params.node)
    }
    const selectedNodes = this.gridApi?.getSelectedNodes()
    const jobIds = selectedNodes?.map(node => node.data.jobId)
    const uniqueJobIds = Array.from(new Set(jobIds)).join(',')

    const menuItems = [
      {
        name: 'Filter on Job',
        action: () => {
          this.selectedCommandsJobIds.emit(uniqueJobIds)
        },
      },
    ]
    if (params.column?.getColId() === 'data')
      menuItems.push({
        name: 'Open in JSON Reader',
        action: () => {
          this.launchJsonReaderModal(params.value)
        },
      })
    return menuItems
  }

  launchJsonReaderModal(data: string) {
    const modalRef = this.modalService.open(JsonReaderModalComponent, {
      size: 'md',
      windowClass: 'modal-window-click-through',
      backdrop: false,
      keyboard: false,
    } as NgbModalOptions)
    modalRef.componentInstance.json =
      JSON.stringify(data)
        .replace(/,/g, ',\n')
        .replace(/{"/g, '{\n"')
        .replace(/}/g, '\n}') ?? ''
  }
}
